import $ from 'jquery';

// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import {FolderSection} from 'constants/FolderConstants';
import OnboardingConstants, {NUEModule, OnboardingPreferences} from 'constants/OnboardingConstants';
import OnboardingDAO from 'dao/OnboardingDAO';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import AppDispatcher from 'lib/Dispatcher';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import notifications from 'notifications';
import preferences from 'Preferences';
import EntityStore from 'stores/EntityStore';
import licenses from 'user/Licenses';
import {formatBytes} from 'util/NameUtil';

export function fetchOnboardingTasks(): void {
  OnboardingDAO.fetchOnboardingTasks().done((response) => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ONBOARDING_FETCH_GETTING_STARTED_TASKS_COMPLETED,
      gettingStarted: response,
    });
  });
}

export function minimizeFileOnboarding(): void {
  preferences.set(OnboardingPreferences.MINIMIZE_FILE_ONBOARDING, true);
}

export function expandFileOnboarding(): void {
  preferences.unset(OnboardingPreferences.MINIMIZE_FILE_ONBOARDING);
}

export function completeOnboardingTask(moduleName: NUEModule, key: string): void {
  if (EntityStore.currentUser() == null) {
    return;
  }

  let isFinalTask = false;
  const module = HomeDashboardStore.getNUEModuleState(moduleName);
  if (module != null && module.tasks) {
    // Don't send the request if we've already completed the task.
    if (module.tasks.some((task: {key: string; completed?: boolean}) => task.key === key && task.completed)) {
      return;
    }

    const unfinishedTasks = module.tasks.filter((task: {completed?: boolean}) => !task.completed);
    isFinalTask = !module.completed && unfinishedTasks.length === 1 && unfinishedTasks[0].key === key;
  }
  OnboardingDAO.completeOnboardingTask(moduleName, key).done((response) => {
    // If the update wasn't successful or no-op'ed (e.g. if the task was already marked complete),
    // return early.
    if (response?.markedComplete === false) {
      return;
    }
    if (isFinalTask && !licenses.hasEnterprisePlan()) {
      const readableModule = OnboardingConstants.READABLE_MODULES[moduleName];
      const bytes = formatBytes(OnboardingConstants.SPACE_ADDITION_SIZES.ONBOARDING[moduleName]);
      notifications.success(`You've successfully completed all ${readableModule} tasks
                               and earned an additional ${bytes} of free space!`);
    }

    AppDispatcher.dispatch({
      actionType: EventConstants.ONBOARDING_COMPLETE_TASK,
      module: moduleName,
      key,
    });

    const module = HomeDashboardStore.getNUEModuleState(moduleName);
    if (module == null) {
      return;
    }

    const taskIndex = module.tasks!.findIndex((task) => task.key === key);
    assert(taskIndex >= 0, `Invalid onboarding task with module=${module} and key=${key}`);

    if (module.openIndex !== taskIndex) {
      return;
    }

    setTimeout(() => {
      if (HomeDashboardStore.getNUEModuleState(moduleName)?.openIndex === taskIndex) {
        setNUEModuleOpenIndex(moduleName, taskIndex + 1);
      }
    }, 500);
  });
}

export function inviteUsers({emails, recaptcha}: {emails: Array<string>; recaptcha: string}): void {
  OnboardingDAO.inviteUsers({emails, recaptcha})
    .done((response) => {
      notifications.success(response!.message);
      UserActions.getReferrals();
    })
    .fail(notifications.notifyError);
}

export function externalInviteUsers({
  emails,
  inviter,
  recaptcha,
}: {
  emails: Array<string>;
  inviter: {email: string; name: string};
  recaptcha: string;
}): void {
  OnboardingDAO.externalInviteUsers({emails, inviter, recaptcha})
    .done((response) => {
      notifications.success(response!.message);
    })
    .fail(notifications.notifyError);
}

export function openSampleEntry(): void {
  $('#fileLoading').removeClass('hidden');
  OnboardingDAO.getOrCreateSampleEntry()
    .done((entry) => {
      const {id, folder} = entry!;
      FileBrowserActions.openFile(id);
      listFolderSection(folder, FolderSection.NOTEBOOK);
    })
    .always(() => $('#fileLoading').addClass('hidden'))
    .fail(notifications.notifyError);
}

export function openSampleSequence(): void {
  $('#fileLoading').removeClass('hidden');
  OnboardingDAO.getOrCreateSampleSequence()
    .done((response) => {
      const {id, folder} = response!;
      FileBrowserActions.openFile(id);
      listFolderSection(folder, FolderSection.INVENTORY);
    })
    .always(() => $('#fileLoading').addClass('hidden'))
    .fail(notifications.notifyError);
}

export function acknowledgeCompletedModule(module: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ONBOARDING_ACKNOWLEDGE_COMPLETED_MODULE,
    module,
  });
}

export function toggleCollapsedNUEModule(module: string, collapsed: boolean): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ONBOARDING_TOGGLE_COLLAPSED_COMPLETED_NUE_MODULE,
    module,
    collapsed,
  });
}

export function setNUEModuleOpenIndex(module: string, openIndex: number | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ONBOARDING_OPEN_NUE_MODULE_INDEX,
    module,
    openIndex,
  });
}

export function dismissNUEModule(module: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ONBOARDING_DISMISS_NUE_MODULE,
    module,
  });
}

export function restoreNUEModule(module: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ONBOARDING_RESTORE_NUE_MODULE,
    module,
  });
}
